import { useEffect, useState, useRef, FC, KeyboardEvent } from 'react';
import { api, Post, resolveMediaUrl } from '../services/api';
import { motion } from 'framer-motion';
import { useLocation } from 'wouter';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { Icon } from './icons/Icon';
import { getCreatorTitle } from '../lib/titles';
import { LikeButton } from './LikeButton';
import { RoleEntryButtons, GenesisRole } from './RoleEntryButtons';

// ─── 三位一体の完成判定（Homeでも使うため公開） ─────────
export const isPostCompleted = (post: Post): boolean => {
    if (!post.roleLimits || !post.childCounts) return false;
    const { text = 0, image = 0, audio = 0 } = post.roleLimits;
    const { text: cText = 0, image: cImage = 0, audio: cAudio = 0 } = post.childCounts;
    return cText >= text && cImage >= image && cAudio >= audio;
};

// ─── Mini Player for listing cards（Heroのショーケースでも使用） ───
export const ListingMiniPlayer: FC<{ src: string; durationSec: number }> = ({ src, durationSec }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!audioRef.current) return;
        if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
        setPlaying(!playing);
    };

    const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

    return (
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} preload="none" />
            <button
                onClick={toggle}
                aria-label={playing ? '一時停止' : '再生'}
                className="w-8 h-8 rounded-full bg-ink text-sun border-2 border-ink flex items-center justify-center hover:-translate-y-0.5 transition-transform"
            >
                <Icon name={playing ? 'pause' : 'play'} size={15} />
            </button>
            <span className="inline-flex items-center gap-1 text-[12px] text-ink-2 font-bold">
                <Icon name="voice" size={14} className="text-role-voice" /> {fmt(durationSec)}
            </span>
        </div>
    );
};

// 役割バッジ: 充足時はアイコンをink色にする（Amber/Teal地×白はコントラスト3:1未満のため。WCAG 1.4.11）
const TrinityBadge: FC<{ name: 'script' | 'art' | 'voice'; on: boolean }> = ({ name, on }) => {
    const color = name === 'script' ? 'bg-role-script' : name === 'art' ? 'bg-role-illustration' : 'bg-role-voice';
    return (
        <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-ink ${on ? `${color} text-ink` : 'bg-white text-ink-2 opacity-50'}`}>
            <Icon name={name} size={13} />
        </span>
    );
};

// ─── ローディングスケルトン（レイアウトシフト防止） ─────────
const SkeletonCard: FC = () => (
    <div className="pop-card overflow-hidden animate-pulse" aria-hidden>
        <div className="w-full h-44 bg-cream-2 border-b-2 border-ink/10" />
        <div className="p-5 space-y-3">
            <div className="h-5 w-2/3 bg-cream-2 rounded" />
            <div className="h-3 w-full bg-cream-2 rounded" />
            <div className="h-3 w-1/2 bg-cream-2 rounded" />
            <div className="flex items-center justify-between pt-2">
                <div className="h-7 w-24 bg-cream-2 rounded-full" />
                <div className="h-9 w-28 bg-cream-2 rounded-full" />
            </div>
        </div>
    </div>
);

interface PostListProps {
    posts: Post[];
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    onCreateGenesis?: (role: GenesisRole) => void;
}

export const PostList: FC<PostListProps> = ({ posts, loading, error, onRetry, onCreateGenesis }) => {
    const [myId, setMyId] = useState<string | null>(null);
    const [likedSet, setLikedSet] = useState<Set<string>>(new Set());
    const [, setLocation] = useLocation();
    const { playSound } = useGamenics();

    useEffect(() => {
        api.getCurrentUser().then(r => { if (r.success && r.data?.id) setMyId(r.data.id); });
    }, []);

    useEffect(() => {
        if (posts.length === 0) return;
        api.getLikedIds(posts.map(p => p.id)).then(ids => setLikedSet(new Set(ids)));
    }, [posts]);

    if (loading) {
        return (
            <section className="w-full max-w-7xl mx-auto px-6 py-20" aria-busy="true" aria-label={COPY.loadingThemes}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <SkeletonCard /><SkeletonCard /><SkeletonCard />
                </div>
            </section>
        );
    }

    // エラーは空状態と明確に区別する（Nielsen #9: エラーの認識・回復）
    if (error) {
        return (
            <section className="w-full max-w-7xl mx-auto px-6 py-20">
                <div className="pop-card max-w-md mx-auto p-8 text-center" role="alert">
                    <p className="font-display font-bold text-ink mb-2">作品を読み込めませんでした</p>
                    <p className="text-sm text-ink-2 mb-5">通信状態を確認して、もう一度お試しください。</p>
                    <button className="btn btn-primary" onClick={onRetry}>
                        <Icon name="repeat" size={16} /> {COPY.retry}
                    </button>
                </div>
            </section>
        );
    }

    if (posts.length === 0) {
        return (
            <section className="w-full max-w-7xl mx-auto px-6 py-20 text-center">
                <p className="text-ink-2 font-display mb-2">{COPY.noItems}</p>
                {onCreateGenesis && (
                    <>
                        <p className="text-sm text-ink-2 mb-5">あなたの得意な役割で、最初の起点作品を生み出そう。</p>
                        <RoleEntryButtons onSelect={onCreateGenesis} />
                    </>
                )}
            </section>
        );
    }

    const completedPosts = posts.filter(isPostCompleted);
    const activePosts = posts.filter(p => !isPostCompleted(p));

    const renderCard = (post: Post, i: number) => {
        const cp = post.childPreview;
        const hasImageChild = !!cp?.image;
        const hasTextChild = !!cp?.text;
        const hasAudioChild = !!cp?.audio;

        // 相対パスのまま <img>/<audio> に渡すと本番(別オリジン)で壊れる → CLAUDE.md §4
        let thumbUrl = '';
        if (hasImageChild && cp?.image?.file_url) {
            thumbUrl = resolveMediaUrl(cp.image.file_url);
        } else if (post.imageUrl) {
            thumbUrl = resolveMediaUrl(post.imageUrl);
        }

        const audioUrl = hasAudioChild && cp?.audio?.file_url
            ? resolveMediaUrl(cp.audio.file_url)
            : '';

        const isCompleted = isPostCompleted(post);
        const open = () => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/post/${post.id}`); };
        const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
            // カード自身へのキー操作のみ反応（内部のボタン操作を横取りしない）
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault();
                open();
            }
        };

        return (
            <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 8) * 0.08 }}
                role="link"
                tabIndex={0}
                aria-label={`${post.title} の詳細を見る`}
                onKeyDown={onKeyDown}
                className="group relative pop-card overflow-hidden cursor-pointer transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                onClick={open}
            >
                {/* ── Image Area ── */}
                <div className="w-full h-44 bg-sun relative overflow-hidden border-b-2 border-ink">
                    {thumbUrl ? (
                        <img src={thumbUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-ink/30">
                            <Icon name="art" size={48} />
                        </div>
                    )}

                    {/* Trinity status badges */}
                    <div className="absolute top-3 right-3 flex gap-1.5">
                        <TrinityBadge name="script" on={hasTextChild} />
                        <TrinityBadge name="art" on={hasImageChild} />
                        <TrinityBadge name="voice" on={hasAudioChild} />
                    </div>

                    {/* Like button */}
                    <div className="absolute bottom-3 left-3">
                        <LikeButton
                            submissionId={post.id}
                            initialCount={post.likeCount || 0}
                            authorId={post.creatorId}
                            myId={myId}
                            isLoggedIn={!!myId}
                            initialLiked={likedSet.has(post.id)}
                            size="sm"
                        />
                    </div>
                </div>

                {/* ── Content ── */}
                <div className="p-5">
                    <h3 className="text-lg font-display font-bold mb-2 text-ink line-clamp-1">
                        {post.title}
                    </h3>

                    {hasTextChild && cp?.text?.description && (
                        <p className="text-ink-2 text-xs mb-2 line-clamp-2 leading-relaxed border-l-2 border-role-script pl-2">
                            {cp.text.description}
                        </p>
                    )}

                    {hasAudioChild && audioUrl && (
                        <div className="mb-3">
                            <ListingMiniPlayer src={audioUrl} durationSec={cp?.audio?.duration_sec || 0} />
                        </div>
                    )}

                    {!hasTextChild && !hasAudioChild && (
                        <p className="text-ink-2 text-xs mb-3 line-clamp-2">
                            {post.description || '（詳細はまだ語られていません）'}
                        </p>
                    )}

                    <div className="flex items-center justify-between border-t-2 border-cream-2 pt-3 mt-1">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-cream-2 border-2 border-ink overflow-hidden">
                                {post.creatorIcon ? (
                                    <img src={post.creatorIcon} alt={post.creatorName} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-primary-soft" />
                                )}
                            </div>
                            <span className="text-xs font-bold text-ink-2 truncate">{post.creatorName}</span>
                            <span className={`text-[10px] font-display font-bold px-1.5 py-0.5 rounded-full border border-ink flex-shrink-0 ${getCreatorTitle(post.creatorLevel || 1).cls}`}>{getCreatorTitle(post.creatorLevel || 1).name}</span>
                        </div>
                        <span className={`btn btn-sm ${isCompleted ? 'btn-primary' : 'btn-pink'}`}>
                            {isCompleted ? '鑑賞する' : COPY.join}
                            <Icon name="arrow-right" size={14} />
                        </span>
                    </div>
                </div>
            </motion.div>
        );
    };

    return (
        <section id="themes" className="w-full max-w-7xl mx-auto px-6 py-20 flex flex-col gap-16">

            {/* Active Works Section（行動を促す主役セクション） */}
            {activePosts.length > 0 && (
                <div>
                    <h2 className="text-center mb-10">
                        <span className="block text-sm text-pop font-sans font-bold tracking-[0.18em] mb-2">ACTIVE WORKS</span>
                        <span className="block text-3xl font-display font-bold text-ink">共創を待つ作品たち</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {activePosts.map((post, i) => renderCard(post, i))}
                    </div>
                </div>
            )}

            {/* Completed Works Section（鑑賞用・控えめな階層） */}
            {completedPosts.length > 0 && (
                <div>
                    <h2 className="text-center mb-10">
                        <span className="block text-sm text-role-voice font-sans font-bold tracking-[0.18em] mb-2">COMPLETED WORKS</span>
                        <span className="block text-2xl font-display font-bold text-ink">三位一体の完成作品</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {completedPosts.map((post, i) => renderCard(post, i))}
                    </div>
                </div>
            )}

        </section>
    );
};
